import type { BylineSsrRoute, BylineSsrSample } from '../ssr/bylineSsrSampleMeasures.ts';

/** Written when the server never reported the sample: it was not run, or the page was sent first. */
export const NOT_RECORDED_OUTCOME = 'not-recorded';

export type BylineSsrSampleAttributes = {
	route: BylineSsrRoute;
	linksToOutcome: string;
	/** `null` when the query was still running as the response was written. */
	linksToDurationMs: number | null;
	appearsInOutcome: string;
	appearsInDurationMs: number | null;
	relatedContentOutcome: string;
	relatedContentDurationMs: number | null;
	/** Rows the graph samples returned; `null` when the sample never settled on the server. CCINT-9673 */
	appearsInRows: number | null;
	appearsInThirdPartyRows: number | null;
	appearsInUnhydratedRows: number | null;
	relatedContentRows: number | null;
	relatedContentThirdPartyRows: number | null;
	relatedContentUnhydratedRows: number | null;
};

type RowCountKind = 'rows' | 'thirdPartyRows' | 'unhydratedRows';

const isRowCountKind = (kind: string): kind is RowCountKind =>
	kind === 'rows' || kind === 'thirdPartyRows' || kind === 'unhydratedRows';

type SampleSection = 'linksTo' | 'appearsIn' | 'relatedContent';

const SECTION_BY_SAMPLE: Record<BylineSsrSample, SampleSection> = {
	outboundBylineSample: 'linksTo',
	incomingLinksSample: 'appearsIn',
	relatedContentSample: 'relatedContent',
};

// Shipped names start `bifrost-ssr`; see `bylineSsrSampleMeasures`.
const SAMPLE_MEASURE_PATTERN =
	/^(?:bifrost-ssr|ssr-app)\/prepare\/(outboundBylineSample|incomingLinksSample|relatedContentSample)\/(view|live)\/(duration|outcome|rows|thirdPartyRows|unhydratedRows)(?::(.+))?$/;

const getDuration = (entry: unknown): number | null => {
	if (typeof entry !== 'object' || entry === null || !('duration' in entry)) {
		return null;
	}
	return typeof entry.duration === 'number' ? entry.duration : null;
};

/**
 * Reads back the outcome and query time the server recorded for each byline sample, from the SSR
 * measures shipped with the page. `undefined` when the server ran none of them.
 */
export const getBylineSsrSampleAttributes = (
	ssrMeasures: object,
): BylineSsrSampleAttributes | undefined => {
	let route: BylineSsrRoute | undefined;
	const outcomes: Partial<Record<SampleSection, string>> = {};
	const durations: Partial<Record<SampleSection, number | null>> = {};
	const rowCounts: Partial<Record<`${SampleSection}:${RowCountKind}`, number>> = {};
	for (const [name, entry] of Object.entries(ssrMeasures)) {
		const match = SAMPLE_MEASURE_PATTERN.exec(name);
		if (!match) {
			continue;
		}
		const section = SECTION_BY_SAMPLE[match[1] as BylineSsrSample];
		route = match[2] as BylineSsrRoute;
		const [, , , kind, value] = match;
		if (kind === 'duration') {
			durations[section] = getDuration(entry);
		} else if (kind === 'outcome' && value !== undefined) {
			outcomes[section] = value;
		} else if (isRowCountKind(kind) && value !== undefined && /^\d+$/.test(value)) {
			rowCounts[`${section}:${kind}`] = Number(value);
		}
	}

	if (route === undefined) {
		return undefined;
	}
	return {
		route,
		linksToOutcome: outcomes.linksTo ?? NOT_RECORDED_OUTCOME,
		linksToDurationMs: durations.linksTo ?? null,
		appearsInOutcome: outcomes.appearsIn ?? NOT_RECORDED_OUTCOME,
		appearsInDurationMs: durations.appearsIn ?? null,
		relatedContentOutcome: outcomes.relatedContent ?? NOT_RECORDED_OUTCOME,
		relatedContentDurationMs: durations.relatedContent ?? null,
		appearsInRows: rowCounts['appearsIn:rows'] ?? null,
		appearsInThirdPartyRows: rowCounts['appearsIn:thirdPartyRows'] ?? null,
		appearsInUnhydratedRows: rowCounts['appearsIn:unhydratedRows'] ?? null,
		relatedContentRows: rowCounts['relatedContent:rows'] ?? null,
		relatedContentThirdPartyRows: rowCounts['relatedContent:thirdPartyRows'] ?? null,
		relatedContentUnhydratedRows: rowCounts['relatedContent:unhydratedRows'] ?? null,
	};
};
